/**
 * Utilidades para reconocer el área pública del visor de documentos.
 *
 * El visor (`#/viewer/document/:uuid`) se abre sin sesión, pero comparte el
 * bootstrap con el resto de la aplicación: MSAL y `AuthService` arrancan
 * también en esa pantalla. Estas funciones existen para que ese arranque pueda
 * abstenerse de navegar o de procesar respuestas de autenticación cuando el
 * usuario está en el visor.
 *
 * Sustituyen a las comprobaciones basadas en `Router.url`: se consultan durante
 * el bootstrap, **antes** de que el router haya resuelto la primera navegación,
 * momento en el que `router.url` todavía vale `/` y la protección fallaba justo
 * cuando hacía falta. Como la aplicación usa `withHashLocation()`, la ruta real
 * vive en el hash; el `pathname` se contempla por si el deep link llega sin él.
 *
 * Ver `specs/SPEC-AC-005-aislamiento-visor-publico.md`.
 */

/** Prefijo de la ruta del visor público, tal como se declara en `app.routes.ts`. */
const PREFIJO_VISOR = '/viewer';

/**
 * Indica si una ruta de aplicación pertenece al visor público.
 *
 * @param ruta Ruta sin hash, con barra inicial (por ejemplo `/viewer/document/abc`)
 * @returns true si la ruta corresponde al área pública del visor
 */
export function esRutaDelVisorPublico(ruta: string): boolean {
  const normalizada = ruta.split('?')[0];
  return (
    normalizada === PREFIJO_VISOR || normalizada.startsWith(`${PREFIJO_VISOR}/`)
  );
}

/**
 * Extrae la ruta de aplicación que hay en la URL actual del navegador.
 *
 * Prioriza el hash porque es donde el router guarda la ruta; si no hay hash,
 * cae al `pathname` para cubrir los deep links que aún no se han normalizado.
 *
 * @returns Ruta con barra inicial, o cadena vacía si no se puede determinar
 */
export function rutaActualDelNavegador(): string {
  if (typeof globalThis.location === 'undefined') {
    return '';
  }

  const { hash, pathname } = globalThis.location;
  const rutaEnHash = hash.startsWith('#') ? hash.slice(1) : hash;

  if (rutaEnHash) {
    return rutaEnHash.startsWith('/') ? rutaEnHash : `/${rutaEnHash}`;
  }

  return pathname || '';
}

/**
 * Indica si el navegador está mostrando el visor público en este momento.
 *
 * Es la comprobación que deben usar los servicios de autenticación antes de
 * navegar: en el visor no hay sesión que recuperar y expulsar al login sería
 * un falso positivo.
 *
 * @returns true si la URL actual corresponde al visor público
 */
export function enVisorPublico(): boolean {
  return esRutaDelVisorPublico(rutaActualDelNavegador());
}
